import T from 'i18n-react';
import React, { Component } from 'react';
import InputMask from 'react-input-mask';
import { PaymentVendorProcessType } from '../../../constants/PaymentVendorProcessTypeConstants';
import * as CommonStore from '../../../store/Common';

const AUTHORIZENET_ALLOWED_HOSTS = ['authorize.net'];

function isAuthorizeNetUrlAllowed(url) {
    try {
        const { protocol, hostname } = new URL(url);
        return (
            protocol === 'https:' &&
            AUTHORIZENET_ALLOWED_HOSTS.some(
                host => hostname === host || hostname.endsWith('.' + host)
            )
        );
    } catch {
        return false;
    }
}

class AuthorizeNetForm extends Component {
    constructor(props) {
        super(props);

        this.cardNumberValueRef = React.createRef();
        this.expirationRef = React.createRef();
        this.cvvRef = React.createRef();

        this.state = {
            sdkLoaded: false,
            apiLoginId: '',
            clientKey: '',
            sdkUrl: '',
            cardAccountName: '',
            bankAccountName: '',
            cardholderName: '',
            nameOnAccount: '',
            routingNumber: '',
            accountNumber: '',
            accountType: 'checking',
            fields: [
                { name: 'cardAccountName', isRequired: true, forPaymentType: 'CreditCard' },
                { name: 'cardholderName', isRequired: true, forPaymentType: 'CreditCard', minTextWords: 2, onValidating: CommonStore.hasMinWordsAmount },
                { name: 'cardNumberValue', isRequired: true, forPaymentType: 'CreditCard', onValidating: CommonStore.isValidCreditCardNumber, isDomValue: true },
                { name: 'expiration', isRequired: true, forPaymentType: 'CreditCard', onValidating: CommonStore.isValidCreditCardExpDate, isDomValue: true },
                { name: 'cvv', isRequired: true, forPaymentType: 'CreditCard', onValidating: CommonStore.isValidCvv, isDomValue: true },
                { name: 'bankAccountName', isRequired: true, forPaymentType: 'ECheck' },
                { name: 'nameOnAccount', isRequired: true, forPaymentType: 'ECheck', minTextWords: 2, onValidating: CommonStore.hasMinWordsAmount },
                { name: 'routingNumber', isRequired: true, forPaymentType: 'ECheck' },
                { name: 'accountNumber', isRequired: true, forPaymentType: 'ECheck' }
            ]
        };

        this.handleLoad = this.handleLoad.bind(this);
    }

    componentDidMount() {
        this._isMounted = true;
        if (this.props.onHideRecessiveFields) {
            this.props.onHideRecessiveFields([
                'cardAccountName',
                'bankAccountName',
                'bankNameOnAccount',
                'cardNameOnAccount'
            ]);
        }

        this.initializeConfig();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    initializeConfig = () => {
        try {
            const { content } = this.props;
            const configB64 = CommonStore.decompressString(content);
            const ahppConfig = JSON.parse(configB64 || '{}');

            if (ahppConfig.ApiLoginID && ahppConfig.Url && ahppConfig.PublicClientKey) {
                this.setState(
                    { apiLoginId: ahppConfig.ApiLoginID, clientKey: ahppConfig.PublicClientKey, sdkUrl: ahppConfig.Url },
                    this.loadAuthorizeNetSDK
                );
            } else {
                if (this.props.setPaymentError) {
                    this.props.setPaymentError(T.translate('accountPay.paymentConfigurationError') || 'Failed to load payment configuration.');
                }
                this.handleLoad();
            }
        } catch (error) {
            if (this.props.setPaymentError) {
                this.props.setPaymentError(T.translate('accountPay.paymentConfigurationError') || 'Failed to load payment configuration.');
            }
            this.handleLoad();
        }
    }

    loadAuthorizeNetSDK = () => {
        const { sdkUrl } = this.state;

        if (!isAuthorizeNetUrlAllowed(sdkUrl)) {
            if (this.props.setPaymentError) {
                this.props.setPaymentError(T.translate('accountPay.paymentConfigurationError') || 'Failed to load payment configuration.');
            }
            this.handleLoad();
            return;
        }

        if (document.getElementById('authorizeNetSdk')) {
            this.setState({ sdkLoaded: true }, this.handleLoad);
            return;
        }

        const script = document.createElement('script');
        script.id = 'authorizeNetSdk';
        script.src = sdkUrl;
        script.type = 'text/javascript';
        script.async = true;
        script.onload = () => {
            if (this._isMounted) {
                this.setState({ sdkLoaded: true }, this.handleLoad);
            }
        };
        script.onerror = () => {
            if (this._isMounted) {
                if (this.props.setPaymentError) {
                    this.props.setPaymentError(T.translate('accountPay.iframeLoadError') || 'Failed to load payment SDK.');
                }
                this.setState({ sdkLoaded: true }, this.handleLoad);
            }
        };
        document.head.appendChild(script);
    }

    onChange = (e) => {
        const { name, value } = e.target;
        const fields = [...this.state.fields];
        const fieldIdx = fields.findIndex(f => f.name === name);

        if (fieldIdx !== -1) {
            const field = { ...fields[fieldIdx] };

            if (field.isRequired && field.isRequiredError && value) {
                if (field.onValidating) {
                    field.onValidating(field, e, this.props);
                } else {
                    field.isRequiredError = false;
                }
            }

            fields[fieldIdx] = field;
            this.setState({ fields });

            if (field.isDomValue) return;
        }

        this.setState({ [name]: value });
    }

    isFormValid = () => {
        const { paymentMethod, paymentVendorProcessTypeID } = this.props;
        const isAutoPayment = paymentVendorProcessTypeID === PaymentVendorProcessType.AutoPayment;
        const nicknameFields = ['cardAccountName', 'bankAccountName'];
        let valid = true;

        const fields = this.state.fields.map((field) => {
            if (field.forPaymentType && field.forPaymentType !== paymentMethod) return field;

            const nextField = { ...field };

            if (nextField.isDomValue) {
                nextField.value = this[nextField.name + 'Ref'].current ? this[nextField.name + 'Ref'].current.value : '';
            } else {
                nextField.value = this.state[nextField.name];
            }

            nextField.isRequiredError = false;
            const effectiveIsRequired = nicknameFields.includes(nextField.name) ? isAutoPayment : nextField.isRequired;

            if (effectiveIsRequired && !nextField.value) {
                nextField.isRequiredError = true;
                valid = false;
            }

            if (nextField.onValidating && nextField.value) {
                valid = nextField.onValidating(nextField, undefined, this.props) && valid;
            }

            if (nextField.isDomValue) delete nextField.value;
            return nextField;
        });

        this.setState({ fields });

        if (valid) {
            if (this.props.setPaymentError) this.props.setPaymentError('');
        } else {
            if (this.props.setPaymentError) this.props.setPaymentError(T.translate('common.thereAreMissingFields'));
        }
        return valid;
    }

    onPayClick = (e) => {
        e.preventDefault();
        if (!this.isFormValid()) return;

        if (window.Accept && typeof window.Accept.dispatchData === 'function') {

            const btnPay = e.target;

            btnPay.disabled = true;
            btnPay.style.opacity = '0.5';
            btnPay.style.cursor = 'not-allowed';

            window.Accept.dispatchData(this.getSecureData(), this.responseHandler);
        } else {
            if (this.props.setPaymentError) {
                this.props.setPaymentError('Payment service is currently unavailable. Please try again later.');
            }
        }
    }

    getSecureData = () => {
        const { paymentMethod } = this.props;
        const { apiLoginId, clientKey } = this.state;

        let authData = { clientKey: clientKey, apiLoginID: apiLoginId };

        if (paymentMethod === 'CreditCard') {
            const cardNumber = this.cardNumberValueRef.current ? this.cardNumberValueRef.current.value.replace(/[\s_-]/g, '') : '';
            const expiration = this.expirationRef.current ? this.expirationRef.current.value : '';
            const cvv = this.cvvRef.current ? this.cvvRef.current.value : '';
            const [expireMonth = '', expireYearShort = ''] = expiration.split('/');
            const expireYear = expireYearShort.trim();

            return {
                authData,
                cardData: {
                    cardNumber,
                    month: expireMonth.trim(),
                    year: expireYear.length === 2 ? `20${expireYear}` : expireYear,
                    cardCode: cvv,
                    fullName: this.state.cardholderName
                }
            };
        } else {
            return {
                authData,
                bankData: {
                    accountNumber: this.state.accountNumber,
                    routingNumber: this.state.routingNumber,
                    nameOnAccount: this.state.nameOnAccount,
                    accountType: this.state.accountType
                }
            };
        }
    }

    responseHandler = (response) => {
        if (response.messages.resultCode === "Error") {
            let errorText = "";
            const messages = response.messages.message;
            if (messages && messages.length) {
                for (let i = 0; i < messages.length; i++) {
                    errorText += messages[i].text + " ";
                }
            } else {
                errorText = "An error occurred with the payment service.";
            }
            if (this.props.setPaymentError) this.props.setPaymentError(errorText);
        } else {
            this.handleSuccess(response);
        }
    }

    handleSuccess = (response) => {
        const { onPaymentSuccess, onPaymentMethodSuccess, paymentVendorProcessTypeID, paymentMethod } = this.props;
        const isCreditCard = paymentMethod === 'CreditCard';
        const holderName = isCreditCard ? this.state.cardholderName : this.state.nameOnAccount;

        const normalizedResponse = {
            vendor: 'AuthorizeNet',
            token: response.opaqueData.dataValue,
            nameOnAccount: holderName,
            operation: isCreditCard ? 'SaveCCToken' : 'SaveACHToken',
            jsonResponse: JSON.stringify(response),
            last4: isCreditCard ? this.cardNumberValueRef.current.value.replace(/[\s_-]/g, '').slice(-4) : this.state.accountNumber.slice(-4),
        };

        const buildPaymentData = (includeNickname = false) => {
            const baseData = {
                ...normalizedResponse,
                holderName: holderName
            };

            if (includeNickname) {
                baseData.nickname = isCreditCard ? this.state.cardAccountName : this.state.bankAccountName;
            }

            if (isCreditCard) {
                baseData.cardNumber = '************' + baseData.last4;
                baseData.cardType = CommonStore.getCardInfo(this.cardNumberValueRef.current.value.replace(/[\s_-]/g, ''), false).name;
                const expirationParts = this.expirationRef.current ? this.expirationRef.current.value.split('/') : [];
                baseData.expire_month = expirationParts[0] ? expirationParts[0].trim() : '';
                const year = expirationParts[1] ? expirationParts[1].trim() : '';
                baseData.expire_year = year.length === 2 ? '20' + year : year;
            } else {
                baseData.accountNumber = '*****' + baseData.last4;
            }

            return baseData;
        };

        if (paymentVendorProcessTypeID === PaymentVendorProcessType.AutoPayment && onPaymentMethodSuccess) {
            onPaymentMethodSuccess(buildPaymentData(true));
        } else if (onPaymentSuccess) {
            onPaymentSuccess(buildPaymentData(false));
        }
    }

    handleLoad() {
        if (this.props.onLoaded) this.props.onLoaded();
    }

    getCreditCardMask = () => {
        const cardNumber = this.cardNumberValueRef.current ? this.cardNumberValueRef.current.value : '';
        return CommonStore.getCardInfo(cardNumber, false).mask;
    }

    getFieldByName = (name) => this.state.fields.find(f => f.name === name) || {};

    renderCreditCard() {
        const { cardholderName, cardAccountName } = this.state;
        const { paymentVendorProcessTypeID } = this.props;

        const ccNicknameField = this.getFieldByName('cardAccountName');
        const ccNameField = this.getFieldByName('cardholderName');
        const cardNumberField = this.getFieldByName('cardNumberValue');
        const expirationField = this.getFieldByName('expiration');
        const cvvField = this.getFieldByName('cvv');

        return (
            <div className="authnet-credit-card">
                {paymentVendorProcessTypeID === PaymentVendorProcessType.AutoPayment &&
                    <div className="row">
                        <div className="col-md-12">
                            <div className="form-group">
                                <label htmlFor="cardAccountName">{T.translate('paymentMethod.cardNickname')}</label>
                                <input type="text" id="cardAccountName" name="cardAccountName" value={cardAccountName} onChange={this.onChange} className={'form-control ' + (ccNicknameField.isRequiredError ? 'is-invalid' : '')} autoComplete="off" />
                                <div className="invalid-feedback">{T.translate('common.thisFieldIsRequired')}</div>
                            </div>
                        </div>
                    </div>
                }
                <div className="row">
                    <div className="col-md-6">
                        <div className="form-group">
                            <label htmlFor="cardholderName">{T.translate('accountPay.nameOnCard')}</label>
                            <input type="text" id="cardholderName" name="cardholderName" value={cardholderName} onChange={this.onChange} className={'form-control ' + (ccNameField.isRequiredError ? 'is-invalid' : '')} autoComplete="off" />
                            <div className="invalid-feedback">{T.translate('accountPay.holderNameRequired')}</div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group">
                            <label htmlFor="cardNumberValue">{T.translate('accountPay.cardNumber')}</label>
                            <InputMask ref={this.cardNumberValueRef} mask={this.getCreditCardMask()} placeholder="#### #### #### ####" id="cardNumberValue" name="cardNumberValue" onChange={this.onChange} className={'form-control ' + (cardNumberField.isRequiredError ? 'is-invalid' : '')} autoComplete="off" />
                            <div className="invalid-feedback">{T.translate('common.thisFieldIsRequired')}</div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-lg-4 col-md-6">
                        <div className="form-group">
                            <label htmlFor="expiration">{T.translate('accountPay.expiration')}</label>
                            <InputMask ref={this.expirationRef} mask="99/99" placeholder="MM/YY" id="expiration" name="expiration" onChange={this.onChange} className={'form-control ' + (expirationField.isRequiredError ? 'is-invalid' : '')} autoComplete="off" />
                            <div className="invalid-feedback">{T.translate('common.thisFieldIsRequired')}</div>
                        </div>
                    </div>
                    <div className="col-lg-4 col-md-6">
                        <div className="form-group">
                            <label htmlFor="cvv">{T.translate('accountPay.securityCode')}</label>
                            <input type="password" inputMode="numeric" pattern="[0-9]*" ref={this.cvvRef} id="cvv" name="cvv" maxLength={4} onChange={this.onChange} className={'form-control ' + (cvvField.isRequiredError ? 'is-invalid' : '')} autoComplete="off" />
                            <div className="invalid-feedback">{T.translate('common.thisFieldIsRequired')}</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderAch() {
        const { accountType, nameOnAccount, routingNumber, accountNumber, bankAccountName } = this.state;
        const { paymentVendorProcessTypeID } = this.props;

        const ccNicknameField = this.getFieldByName('bankAccountName');
        const achNameField = this.getFieldByName('nameOnAccount');
        const routingField = this.getFieldByName('routingNumber');
        const accountNumberField = this.getFieldByName('accountNumber');

        return (
            <div className="authnet-ach">
                {paymentVendorProcessTypeID === PaymentVendorProcessType.AutoPayment &&
                    <div className="row">
                        <div className="col-md-12">
                            <div className="form-group">
                                <label htmlFor="bankAccountName">{T.translate('paymentMethod.bankAccountNickname')}</label>
                                <input type="text" id="bankAccountName" name="bankAccountName" value={bankAccountName} onChange={this.onChange} className={"form-control " + (ccNicknameField.isRequiredError ? 'is-invalid' : '')} autoComplete="off" />
                                <div className="invalid-feedback">{T.translate('common.thisFieldIsRequired')}</div>
                            </div>
                        </div>
                    </div>
                }
                <div className="row">
                    <div className="col-md-6">
                        <div className="form-group">
                            <label htmlFor="nameOnAccount">{T.translate('autopay.accountHolderName')}</label>
                            <input type="text" id="nameOnAccount" name="nameOnAccount" value={nameOnAccount} onChange={this.onChange} className={'form-control ' + (achNameField.isRequiredError ? 'is-invalid' : '')} autoComplete="off" />
                            <div className="invalid-feedback">{T.translate('accountPay.holderNameRequired')}</div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group">
                            <label htmlFor="routingNumber">{T.translate('accountPay.routingNumber')}</label>
                            <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={9} id="routingNumber" name="routingNumber" value={routingNumber} onChange={this.onChange} className={'form-control ' + (routingField.isRequiredError ? 'is-invalid' : '')} autoComplete="off" />
                            <div className="invalid-feedback">{T.translate('common.thisFieldIsRequired')}</div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <div className="form-group">
                            <label htmlFor="accountNumber">{T.translate('accountPay.bankAccountNumber')}</label>
                            <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={17} id="accountNumber" name="accountNumber" value={accountNumber} onChange={this.onChange} className={'form-control ' + (accountNumberField.isRequiredError ? 'is-invalid' : '')} autoComplete="off" />
                            <div className="invalid-feedback">{T.translate('common.thisFieldIsRequired')}</div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group">
                            <label htmlFor="accountType">{T.translate('autopay.bankAccountType')}</label>
                            <select id="accountType" name="accountType" value={accountType} onChange={this.onChange} className="form-control">
                                <option value="checking">{T.translate('payment.bankTypes.checking')}</option>
                                <option value="savings">{T.translate('payment.bankTypes.savings')}</option>
                                <option value="businessChecking">{T.translate('payment.bankTypes.businessChecking')}</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { paymentMethod } = this.props;

        if (!this.state.sdkLoaded) return null;

        const paymentMethodText = paymentMethod ? paymentMethod.toLowerCase() : paymentMethod;

        return (
            <div className="authorizenet-wrapper">
                {paymentMethodText === 'creditcard' && this.renderCreditCard()}
                {paymentMethodText === 'echeck' && this.renderAch()}

                <div className="text-right mt-3">
                    <button className="btn btn-primary" onClick={this.onPayClick} title="Authorize.Net Payment" >
                        {T.translate('accountPay.submitPayment')}
                    </button>
                </div>
            </div>
        );
    }
}

export default AuthorizeNetForm;
